import { t } from 'i18next'
import { handleSpecialCaseDetailArrays } from 'utils/tinyMCE/handleSpecialCaseDetailArrays'
import { tableTemplate, buildHonoraryPallbearersBlock } from '.'
import { insertTemplate } from '../insertMenu/insertTemplate'

/**
 * Emits up to two independent "Insert detail" menu items:
 *  - "Pallbearers": regular pallbearers only.
 *  - "Honorary Pallbearers": honorary pallbearers only.
 * Honorary names come from the PallBearer relation (type === 'honorary pallbearer')
 * embedded in the `pallbearers` array, plus the legacy `honorary_pallbearers` list.
 */
export const handlePallbearersArray = (
  editor: Editor,
  caseDetails: Record<string, unknown>,
  key: string,
  templates: Array<{ type: string; text: string; onAction: () => void }>,
  _dateFormat: string,
  selectedTheme: unknown,
) => {
  // When both `pallbearers` and `honorary_pallbearers` exist in caseDetails,
  // the `pallbearers` pass already builds the combined block. Skip the
  // `honorary_pallbearers` pass to avoid a duplicate menu entry.
  if (key === 'honorary_pallbearers' && 'pallbearers' in caseDetails) {
    return
  }

  const pallbearers = caseDetails[key] as Array<{
    name: string
    type?: string
  }> | null

  const regularNames = handleSpecialCaseDetailArrays({
    path: 'pallbearers',
    items: pallbearers,
  })

  const honoraryFromRelation = handleSpecialCaseDetailArrays({
    path: 'honorary_pallbearers_from_relation',
    items: pallbearers,
  })

  const honoraryFromLegacy = Array.isArray(caseDetails.honorary_pallbearers)
    ? caseDetails.honorary_pallbearers.filter(
        (item): item is string => typeof item === 'string',
      )
    : []

  const allHonorary = [...(honoraryFromRelation || []), ...honoraryFromLegacy]

  const hasRegular = regularNames && regularNames.length > 0
  const hasHonorary = allHonorary.length > 0

  if (!hasRegular && !hasHonorary) return

  // Regular pallbearers: their own menu item, regular names only.
  if (hasRegular) {
    const regularHtml = tableTemplate({
      path: 'pallbearers',
      items: regularNames,
      selectedTheme,
      editor,
    })

    templates.push({
      type: 'menuitem',
      text: t('caseFormSectionsLabels.Pallbearers Header'),
      onAction: () => insertTemplate(editor, regularHtml),
    })
  }

  // Honorary pallbearers: a separate menu item, honorary names only.
  if (hasHonorary) {
    const honoraryHtml = buildHonoraryPallbearersBlock({
      honoraryNames: allHonorary,
      selectedTheme,
      editor,
    })

    templates.push({
      type: 'menuitem',
      text: t('caseFormSectionsLabels.Honorary Pallbearers Heading'),
      onAction: () => insertTemplate(editor, honoraryHtml),
    })
  }
}

export default handlePallbearersArray
